import type { CredentialEnvelope, ExternalCredential, SealedReply } from '@/features/external-account/types';

/**
 * 방향별 HKDF info — 서버(`external-account/infrastructure/crypto/ecdh-transport.cipher.ts`)와 같은 값이어야 한다.
 * 한쪽만 바꾸면 모든 요청이 "풀 수 없다"로 실패한다.
 */
const INFO = {
  request: 'external-account/transport/request/v1',
  reply: 'external-account/transport/reply/v1',
} as const;

const CURVE = { name: 'ECDH', namedCurve: 'P-256' } as const;

/**
 * 아이디·비밀번호를 서버와 주고받을 때 HTTPS 위에 한 겹 더 잠근다(ECDH P-256 + HKDF-SHA256 + AES-GCM).
 *
 * 요청마다 일회용 키쌍을 만든다. 비밀키는 추출할 수 없게 만들고 이 요청이 끝나면 버린다 — 응답을
 * 풀 수 있는 것은 그 요청을 보낸 이 화면뿐이다.
 */
export class CredentialTransport {
  /** 보낼 자격증명을 서버 공개키로 잠근다. */
  static async seal(serverPublicKey: string, credential: ExternalCredential): Promise<CredentialEnvelope> {
    const pair = await CredentialTransport.ephemeralPair();
    const key = await CredentialTransport.sessionKey(pair.privateKey, await CredentialTransport.importPoint(serverPublicKey), 'request');
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(JSON.stringify(credential)));
    return {
      ephemeralKey: CredentialTransport.base64(await crypto.subtle.exportKey('raw', pair.publicKey)),
      iv: CredentialTransport.base64(iv),
      data: CredentialTransport.base64(data),
    };
  }

  /**
   * 응답을 받을 일회용 키쌍을 만들고, 그 공개키를 실은 요청(`request`)이 돌려준 묶음을 푼다.
   * 서버는 이 공개키와 자기 비밀키로, 화면은 이 비밀키와 서버 공개키로 같은 공유 비밀을 만든다.
   */
  static async receive(request: (ephemeralKey: string) => Promise<SealedReply>, serverPublicKey: string): Promise<ExternalCredential> {
    const pair = await CredentialTransport.ephemeralPair();
    const reply = await request(CredentialTransport.base64(await crypto.subtle.exportKey('raw', pair.publicKey)));
    const key = await CredentialTransport.sessionKey(pair.privateKey, await CredentialTransport.importPoint(serverPublicKey), 'reply');
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: CredentialTransport.bytes(reply.iv) },
      key,
      CredentialTransport.bytes(reply.data),
    );
    return JSON.parse(new TextDecoder().decode(plain)) as ExternalCredential;
  }

  static ephemeralPair(): Promise<CryptoKeyPair> {
    return crypto.subtle.generateKey(CURVE, false, ['deriveBits']);
  }

  static importPoint(publicKey: string): Promise<CryptoKey> {
    return crypto.subtle.importKey('raw', CredentialTransport.bytes(publicKey), CURVE, false, []);
  }

  static async sessionKey(privateKey: CryptoKey, peer: CryptoKey, direction: keyof typeof INFO): Promise<CryptoKey> {
    const shared = await crypto.subtle.deriveBits({ name: 'ECDH', public: peer }, privateKey, 256);
    const hkdf = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
    return crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: new TextEncoder().encode(INFO[direction]) },
      hkdf,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  static base64(bytes: ArrayBuffer | Uint8Array): string {
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    return btoa(String.fromCharCode(...view));
  }

  static bytes(base64: string): Uint8Array<ArrayBuffer> {
    return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  }
}
